import { useRef, useState, type FormEvent } from "react";
import { ScanLine, Check, Plus } from "lucide-react";
import { Button, EmptyState, Field, Icon, Sheet, Tag } from "@cntrl/ui";
import { useMobileBack } from "../../app/mobileBack.js";
import { useLiveGroup } from "../../app/realtime/RealtimeProvider.js";
import { useCapability } from "../../app/bandContext.js";
import {
  useGuestList,
  useAddGuest,
  useGuestPartners,
  useScanGuest,
  guestCounts,
  guestSeats,
  type GuestPartnerOption,
} from "../../app/hooks/useGuestList.js";
import { PartnerChips } from "./PartnerChips.js";
import { GuestLoading, GuestQueryError } from "./GuestListStates.js";
import styles from "./DoorCheckInScreen.module.scss";
import { partnerRelationshipLabel } from "../../app/partnerRelationship.js";

export interface DoorCheckInScreenProps {
  showId: string;
  onBack: () => void;
}

/**
 * Mobile door check-in (the `/shows/:id/guests` mobile route). The "phone at the door" of the
 * scripted demo: joins show:{id}, shows a live arrived/comps banner and the guest rows, and each row scans
 * on tap (server-confirm THEN repaint — scan is not optimistic). A scan here flips the desktop
 * modal live (step 2); the arrived count moves, comps does not. All roles except Content reach
 * this; scanning needs guests:scan and adding needs guests:write (server-enforced — each affordance hides
 * without its token).
 */
export function DoorCheckInScreen({ showId, onBack }: DoorCheckInScreenProps) {
  useLiveGroup(`show:${showId}`);
  // A DOS sub-page: back rides the shell header's leading chevron (useMobileBack), like the VIP + Logistics
  // sub-screens — so Guest List reads as a page you back out of, not a takeover with only a "Done" button
  // (ART-174 feedback). The in-body "Done" stays as the door task's own finish affordance.
  useMobileBack(onBack);

  const list = useGuestList(showId);
  const scanGuest = useScanGuest(showId);
  const canScan = useCapability("guests:scan");
  const canAdd = useCapability("guests:write");
  const counts = guestCounts(list.data);
  const rows = list.data?.items ?? [];

  const [addOpen, setAddOpen] = useState(false);

  return (
    <div className={styles.screen}>
      <header className={styles.banner}>
        <div className={styles.bannerTop}>
          <span className={styles.bannerLabel}>
            <Icon icon={ScanLine} size={16} /> Door check-in
          </span>
          <button type="button" className={styles.done} onClick={onBack}>
            Done
          </button>
        </div>
        <p className={styles.arrived}>
          <strong data-testid="arrived-count">{counts.arrived}</strong>
          <span className={styles.arrivedOf}>
            {" / "}
            <span data-testid="comps-count">{counts.comps}</span> arrived
          </span>
        </p>
      </header>

      <div className={styles.body}>
        {list.isLoading ? (
          <GuestLoading label="Loading door list…" />
        ) : list.isError ? (
          <GuestQueryError error={list.error} onRetry={() => void list.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState title="No guests on the list" />
        ) : (
          <ul className={styles.rows}>
            {rows.map((row) => {
              const scanned = row.scannedOn != null;
              const scanning = scanGuest.isPending && scanGuest.variables === row.guestId;
              return (
                <li key={row.guestId} className={styles.row} data-scanned={scanned || undefined}>
                  <div className={styles.rowMain}>
                    <span className={styles.rowName}>{row.name || "Guest"}</span>
                    {row.source === "partner" && <Tag size="sm">{partnerRelationshipLabel(row.partnerRelationship) ?? "Partner"}</Tag>}
                    {(row.plusCount ?? 0) > 0 && (
                      <span className={styles.party}>party of {guestSeats(row)}</span>
                    )}
                  </div>
                  {scanned ? (
                    <span className={styles.checkedIn}>
                      <Icon icon={Check} size={16} /> In
                    </span>
                  ) : canScan ? (
                    <Button
                      size="sm"
                      loading={scanning}
                      onClick={() => row.guestId && scanGuest.mutate(row.guestId)}
                    >
                      Scan in
                    </Button>
                  ) : (
                    <span className={styles.waiting}>Not in</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Add people to the list from the door — a big crimson + at the bottom (Ryan, ART-174). Gated on
          guests:write, which the server enforces on the POST; a read-only seat never sees the button. */}
      {canAdd && (
        <div className={styles.addBar}>
          <button type="button" className={styles.addBtn} onClick={() => setAddOpen(true)}>
            <Icon icon={Plus} size={20} /> Add guest
          </button>
        </div>
      )}

      {addOpen && <AddGuestSheet showId={showId} onClose={() => setAddOpen(false)} />}
    </div>
  );
}

/** The door-side add-guest form — a bottom sheet with name, plus-ones and an optional note, and the partner chips
 *  (ART-239 part 3): a chosen partner fills the name and locks it. */
function AddGuestSheet({ showId, onClose }: { showId: string; onClose: () => void }) {
  const addGuest = useAddGuest(showId);
  const partners = useGuestPartners(showId);
  const [partner, setPartner] = useState<GuestPartnerOption | null>(null);
  const [name, setName] = useState("");
  const [plusCount, setPlusCount] = useState("0");
  const [note, setNote] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);

  function onAdd(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      nameRef.current?.focus();
      return;
    }
    const plus = Math.max(0, Math.trunc(Number(plusCount) || 0));
    addGuest.mutate(
      { name: trimmed, plusCount: plus, note: note.trim() || null, partnerLinkId: partner?.partnerLinkId ?? null },
      { onSuccess: onClose },
    );
  }

  return (
    <Sheet title="Add guest" onClose={onClose}>
      <form className={styles.addForm} onSubmit={onAdd}>
        <PartnerChips
          options={partners.data}
          selected={partner?.partnerLinkId ?? null}
          onSelect={(o) => {
            setPartner(o);
            setName(o?.guestName ?? "");
          }}
        />
        <div className={styles.addRow}>
          <Field
            ref={nameRef}
            label="Guest name"
            placeholder="Name"
            value={name}
            readOnly={partner !== null}
            onChange={(e) => setName(e.target.value)}
            className={styles.addName}
          />
          <Field
            label="+"
            type="number"
            min={0}
            inputMode="numeric"
            value={plusCount}
            onChange={(e) => setPlusCount(e.target.value)}
            className={styles.addPlus}
            aria-label="Plus ones"
          />
        </div>
        <Field
          label="Note"
          placeholder="Optional"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Button
          type="submit"
          size="md"
          fullWidth
          className={styles.addSubmit}
          loading={addGuest.isPending}
          disabled={!name.trim()}
        >
          <Icon icon={Plus} size={16} /> Add guest
        </Button>
      </form>
    </Sheet>
  );
}
